import { VerificationModel } from 'api/models';
import clsx from 'clsx';
import { startCase } from 'es-toolkit';

type ItemStatus = string;

interface StatusListProps {
  items: VerificationModel[];
  className?: string;
}

type VerificationType =
  | 'Identity'
  | 'Income'
  | 'Criminal'
  | 'Eviction'
  | 'Credit'
  | 'Reference';

const statusRightTextClass: Record<ItemStatus, string> = {
  Passed: 'text-green',
  Processing: 'text-orange',
  RequiresManualReview: 'text-blue',
  Failed: 'text-blue',
  Rejected: 'text-blue',
  Pending: 'text-orange',
  Disabled: 'text-dark-gray',
};

function prettyStatus(s: ItemStatus): string {
  switch (s) {
    case 'Passed':
      return 'Passed';
    case 'Processing':
      return 'Processing';
    case 'done':
      return 'Done';
    case 'RequiresManualReview':
      return 'Reviewing';
    case 'Rejected':
      return 'Reviewing';
    case 'Failed':
      return 'Reviewing';
    case 'Pending':
      return 'Processing';
    default:
      return startCase(s);
  }
}

function normalizeVerifications(items: VerificationModel[]) {
  const filtered = items.filter(
    (v) => !(v.verificationType === 'Income' && v.status === 'Disabled'),
  );

  const presentTypes = new Set(filtered.map((v) => v.verificationType));

  const allTypes: VerificationType[] = [
    'Identity',
    'Income',
    'Criminal',
    'Eviction',
    'Credit',
    'Reference',
  ];

  // `isValid` and the duplicate `type` are not on `VerificationModel` and
  // nothing reads them, but they are kept rather than dropped so the shape this
  // hands back is unchanged.
  const result: (VerificationModel & {
    isValid?: boolean;
    type?: VerificationType;
  })[] = [...filtered];
  allTypes.forEach((type: VerificationType) => {
    if (!presentTypes.has(type)) {
      result.push({
        id: `pending-${type}`,
        userId: 0,
        verificationType: type as VerificationType,
        isValid: false,
        status: 'Pending',
        type: type as VerificationType,
      });
    }
  });

  return result;
}

export default function ApplicationVerificationsStatus({
  items,
  className,
}: StatusListProps) {
  const normalizedItems = normalizeVerifications(items);
  return (
    <div className={clsx('w-full py-sm', className)}>
      <ul className="flex flex-col gap-xs lg:w-full lg:flex-row lg:justify-between">
        {normalizedItems.map((item) => {
          const { status } = item;
          const rightTextCls = statusRightTextClass[status];
          return (
            <li
              key={item.id}
              className="flex items-center justify-between lg:w-[114px] lg:flex-col lg:items-start"
            >
              <div className="flex items-center gap-xs">
                <span className="text-sm font-semibold tracking-sm text-navy">
                  {startCase(item.verificationType)}
                </span>
              </div>

              <span className={clsx('text-sm font-semibold', rightTextCls)}>
                {prettyStatus(item.status)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
